// Admin-only professional Floor Plan Builder.
// - Pre-upload client form
// - Upload PNG/JPG/PDF background
// - Drag-drop NYC NEC standard symbols (resize, rotate, delete)
// - Text/arrow/rectangle/measurement annotations
// - Auto legend
// - AI cost estimate (GPT-4 + NYC catalog)
// - Professional PDF export with company logo + breakdown
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Download, Sparkles, Trash2, Type, Square, ArrowRight,
  Ruler, MousePointer2, Loader2, Plus, Minus, RotateCw, Image as ImageIcon, X, Send, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { SYMBOL_LIBRARY, SYMBOL_CATEGORIES, SymbolSVG } from './FloorPlanSymbols';
import { generateFloorPlanPdf } from './floorPlanPdf';

const FORM_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'email', label: 'Email', required: true, type: 'email' },
  { key: 'phone', label: 'Phone', required: true, type: 'tel' },
  { key: 'company', label: 'Company (optional)' },
  { key: 'address', label: 'Project Address', required: true },
  { key: 'project_name', label: 'Project Name', required: true },
];

const TOOLS = [
  { id: 'select', icon: MousePointer2, label: 'Select' },
  { id: 'text', icon: Type, label: 'Text' },
  { id: 'rect', icon: Square, label: 'Rectangle' },
  { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
  { id: 'measure', icon: Ruler, label: 'Measure' },
];

const AdminFloorPlanBuilder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | builder
  const [propertyType, setPropertyType] = useState('residential');
  const [client, setClient] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', address: '', project_name: '' });
  const [bgImage, setBgImage] = useState(null);
  const [pins, setPins] = useState([]); // {id, symbolKey, x, y, scale, rotation}
  const [annotations, setAnnotations] = useState([]); // {id, type, x, y, x2, y2, text, color}
  const [selectedId, setSelectedId] = useState(null);
  const [activeTool, setActiveTool] = useState('select');
  const [placingSymbol, setPlacingSymbol] = useState(null);
  const [activeCategory, setActiveCategory] = useState('electrical');
  const [drawing, setDrawing] = useState(null); // current annotation being drawn
  const [dragInfo, setDragInfo] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [savingRequest, setSavingRequest] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [priceOverrides, setPriceOverrides] = useState({}); // {symbolKey: customPrice}
  const [taxRate, setTaxRate] = useState(0.08875); // editable NYC tax
  const [contingencyRate, setContingencyRate] = useState(0.10);
  const [taxBase, setTaxBase] = useState(0.4); // % of subtotal subject to tax
  const canvasRef = useRef(null);

  const updateClient = (k, v) => setClient(c => ({ ...c, [k]: v }));
  const submitForm = (e) => {
    e.preventDefault();
    const missing = FORM_FIELDS.filter(f => f.required && !client[f.key]?.trim());
    if (missing.length) return toast.error(`Please fill: ${missing.map(m => m.label).join(', ')}`);
    setStep('builder');
  };

  // --- Background upload ---
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    try {
      if (file.type === 'application/pdf') {
        await loadPdfAsImage(file);
      } else if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setBgImage({ url, name: file.name });
      } else {
        toast.error('Upload an image (PNG/JPG) or PDF');
      }
    } catch (err) {
      toast.error('Failed to load file: ' + err.message);
    } finally {
      setUploadingBg(false);
    }
  };

  const loadPdfAsImage = async (file) => {
    // Lazy-load pdfjs
    const pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');
    // Use CDN worker matching the lib version to avoid bundling issues
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
    const arr = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arr }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 2 });
    const c = document.createElement('canvas');
    c.width = viewport.width;
    c.height = viewport.height;
    await page.render({ canvasContext: c.getContext('2d'), viewport }).promise;
    setBgImage({ url: c.toDataURL('image/png'), name: file.name, fromPdf: true });
  };

  // --- Place symbol from sidebar ---
  const startPlace = (symbolKey) => {
    setPlacingSymbol(symbolKey);
    setActiveTool('select');
    toast.info('Click on the plan to place the symbol');
  };

  // --- Canvas events ---
  const canvasPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const handleCanvasMouseDown = (e) => {
    if (!bgImage) return;
    const pt = canvasPoint(e);
    if (placingSymbol) {
      setPins(p => [...p, { id: Date.now(), symbolKey: placingSymbol, x: pt.x, y: pt.y, scale: 1, rotation: 0 }]);
      setPlacingSymbol(null);
      return;
    }
    if (activeTool === 'text') {
      const text = window.prompt('Enter text (e.g. "Master Bedroom"):', '');
      if (text) setAnnotations(a => [...a, { id: Date.now(), type: 'text', x: pt.x, y: pt.y, text, color: '#dc2626' }]);
      return;
    }
    if (['rect', 'arrow', 'measure'].includes(activeTool)) {
      setDrawing({ id: Date.now(), type: activeTool, x: pt.x, y: pt.y, x2: pt.x, y2: pt.y, color: '#dc2626' });
      return;
    }
    // click empty area deselects
    setSelectedId(null);
  };

  const handleCanvasMouseMove = (e) => {
    if (!bgImage) return;
    const pt = canvasPoint(e);
    if (drawing) {
      setDrawing(d => ({ ...d, x2: pt.x, y2: pt.y }));
      return;
    }
    if (dragInfo) {
      const dx = pt.x - dragInfo.startX;
      const dy = pt.y - dragInfo.startY;
      if (dragInfo.kind === 'pin') {
        setPins(ps => ps.map(p => p.id === dragInfo.id ? { ...p, x: Math.max(0, Math.min(100, dragInfo.origX + dx)), y: Math.max(0, Math.min(100, dragInfo.origY + dy)) } : p));
      } else if (dragInfo.kind === 'annot') {
        setAnnotations(as => as.map(a => {
          if (a.id !== dragInfo.id) return a;
          if (a.type === 'text') return { ...a, x: dragInfo.origX + dx, y: dragInfo.origY + dy };
          return { ...a, x: dragInfo.origX + dx, y: dragInfo.origY + dy, x2: dragInfo.origX2 + dx, y2: dragInfo.origY2 + dy };
        }));
      }
    }
  };

  const handleCanvasMouseUp = () => {
    if (drawing) {
      // Skip zero-size shapes
      if (Math.abs(drawing.x - drawing.x2) > 0.5 || Math.abs(drawing.y - drawing.y2) > 0.5) {
        setAnnotations(a => [...a, drawing]);
      }
      setDrawing(null);
    }
    setDragInfo(null);
  };

  const startDragPin = (e, pin) => {
    if (activeTool !== 'select') return;
    e.stopPropagation();
    const pt = canvasPoint(e);
    setSelectedId(pin.id);
    setDragInfo({ kind: 'pin', id: pin.id, startX: pt.x, startY: pt.y, origX: pin.x, origY: pin.y });
  };
  const startDragAnnot = (e, ann) => {
    if (activeTool !== 'select') return;
    e.stopPropagation();
    const pt = canvasPoint(e);
    setSelectedId(ann.id);
    setDragInfo({ kind: 'annot', id: ann.id, startX: pt.x, startY: pt.y, origX: ann.x, origY: ann.y, origX2: ann.x2, origY2: ann.y2 });
  };

  // --- Selected item manipulation ---
  const selectedPin = pins.find(p => p.id === selectedId);
  const selectedAnnot = annotations.find(a => a.id === selectedId);
  const adjustScale = (delta) => selectedPin && setPins(ps => ps.map(p => p.id === selectedPin.id ? { ...p, scale: Math.max(0.3, Math.min(3, p.scale + delta)) } : p));
  const rotateSelected = () => selectedPin && setPins(ps => ps.map(p => p.id === selectedPin.id ? { ...p, rotation: (p.rotation + 45) % 360 } : p));
  const deleteSelected = () => {
    if (!selectedId) return;
    setPins(ps => ps.filter(p => p.id !== selectedId));
    setAnnotations(as => as.filter(a => a.id !== selectedId));
    setSelectedId(null);
  };
  const editTextAnnot = () => {
    if (!selectedAnnot || selectedAnnot.type !== 'text') return;
    const t = window.prompt('Edit text:', selectedAnnot.text);
    if (t != null) setAnnotations(as => as.map(a => a.id === selectedAnnot.id ? { ...a, text: t } : a));
  };
  const clearAll = () => {
    if (window.confirm('Remove all symbols and annotations?')) { setPins([]); setAnnotations([]); setEstimate(null); }
  };

  // --- Legend & summary ---
  const symbolCounts = pins.reduce((acc, p) => {
    acc[p.symbolKey] = (acc[p.symbolKey] || 0) + 1;
    return acc;
  }, {});
  const priceFor = (k) => priceOverrides[k] != null ? Number(priceOverrides[k]) : SYMBOL_LIBRARY[k].price;
  const calcTotals = () => {
    const sub = Object.entries(symbolCounts).reduce((s, [k, q]) => s + priceFor(k) * q, 0);
    const tx = sub * taxBase * taxRate;
    const co = sub * contingencyRate;
    return { sub, tx, co, total: sub + tx + co };
  };

  // --- Save as service request ---
  const saveAsRequest = async () => {
    if (pins.length === 0) return toast.error('Place at least one symbol first');
    setSavingRequest(true);
    try {
      const items = Object.entries(symbolCounts).map(([k, q]) => `${q}x ${SYMBOL_LIBRARY[k].name}`).join('; ');
      const { total } = calcTotals();
      const payload = {
        customer_name: `${client.first_name} ${client.last_name}`.trim(),
        customer_email: client.email,
        customer_phone: client.phone,
        customer_address: client.address,
        service_type: 'electrical',
        description: `[Floor Plan] ${client.project_name}. Items: ${items}`,
        priority: 'medium',
        address: client.address,
        property_type: propertyType,
        price: parseFloat(total.toFixed(2)),
        price_notes: `Floor Plan estimate. Subtotal ${calcTotals().sub.toFixed(2)}, NYC Tax ${calcTotals().tx.toFixed(2)}, Contingency ${calcTotals().co.toFixed(2)}.`,
        source: 'floor_plan',
      };
      const res = await api('/api/admin/service-requests', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`Saved as ${res.ticket_number}`);
    } catch (err) { toast.error('Failed: ' + err.message); }
    finally { setSavingRequest(false); }
  };

  // --- AI Cost estimate ---
  const runEstimate = async () => {
    if (pins.length === 0) { toast.error('Place at least one symbol first'); return; }
    setEstimating(true);
    try {
      const items = Object.entries(symbolCounts).map(([key, qty]) => {
        const s = SYMBOL_LIBRARY[key];
        return `${qty}x ${s.name} (${s.desc})`;
      }).join('; ');
      const description = `${client.project_name} at ${client.address}. Floor plan items: ${items}. Annotations: ${annotations.length} placed.`;
      const data = await api('/api/services/estimate', {
        method: 'POST',
        body: JSON.stringify({ description, property_type: propertyType, address: client.address }),
      });
      setEstimate(data);
      toast.success('AI estimate ready');
    } catch (err) {
      toast.error('Estimate failed: ' + err.message);
    } finally {
      setEstimating(false);
    }
  };

  // PDF export (extracted to ./floorPlanPdf.js so this component stays small)
  // Returns null on disk-save, or {blob, filename} when returnBlob=true.
  const exportPDF = async (returnBlob = false) => {
    if (!canvasRef.current) return null;
    setGenerating(true);
    try {
      const result = await generateFloorPlanPdf({
        canvasRef,
        client,
        propertyType,
        pins,
        symbolCounts,
        annotations,
        estimate,
        totals: calcTotals(),
        priceFor,
        taxRate,
        taxBase,
        contingencyRate,
        returnBlob,
      });
      if (!returnBlob) toast.success('PDF downloaded');
      return result;
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed: ' + err.message);
      return null;
    } finally {
      setGenerating(false);
    }
  };

  // --- Email PDF to client (via SendGrid through backend) ---
  const previewPDF = async () => {
    if (!bgImage) return toast.error('Upload a floor plan first');
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch {}
      setPreviewUrl(null);
    }
    setPreviewing(true);
    try {
      const result = await exportPDF(true);
      if (!result || !result.blob) return;
      const url = URL.createObjectURL(result.blob);
      setPreviewUrl(url);
    } catch (err) {
      toast.error('Preview failed: ' + err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch {}
    }
    setPreviewUrl(null);
  };

  // --- Email PDF to client (via SendGrid through backend) ---
  const emailPDFToClient = async () => {
    if (!client.email) return toast.error('Client email is required to send the PDF');
    if (pins.length === 0) return toast.error('Place at least one symbol first');
    setEmailing(true);
    try {
      const result = await exportPDF(true);
      if (!result || !result.blob) return;
      const blob = result.blob;
      // Convert Blob -> base64 (without data URI prefix)
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const s = reader.result || '';
          const idx = String(s).indexOf(',');
          resolve(idx >= 0 ? String(s).slice(idx + 1) : String(s));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const payload = {
        to_email: client.email,
        cc_admin: true,
        pdf_base64: base64,
        pdf_filename: result.filename,
        project_name: client.project_name,
        client_first_name: client.first_name,
        message: emailMessage || undefined,
      };
      await api('/api/floor-plan/email', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`Floor plan emailed to ${client.email}`);
      setEmailModalOpen(false);
      setEmailMessage('');
    } catch (err) {
      toast.error('Email failed: ' + err.message);
    } finally {
      setEmailing(false);
    }
  };

  // ---- STEP 1: CLIENT FORM ----
  if (step === 'form') {
    return (
      <div className="max-w-2xl mx-auto" data-testid="floorplan-form-step">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">FLOOR PLAN BUILDER</p>
            <h1 className="font-heading text-3xl font-bold text-white">New Project Details</h1>
            <p className="text-sm text-gray-500 mt-1">Enter client information before uploading the floor plan</p>
          </div>
          <button onClick={() => navigate('/admin')} className="btn-secondary text-sm flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Back</button>
        </div>
        <form onSubmit={submitForm} className="card p-6 space-y-4" data-testid="floorplan-client-form">
          <div className="flex gap-2 mb-2">
            {['residential', 'commercial'].map(t => (
              <button key={t} type="button" onClick={() => setPropertyType(t)}
                className={`flex-1 py-2 text-sm font-medium rounded transition-all ${propertyType === t ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                data-testid={`fp-prop-${t}`}>
                {t === 'residential' ? 'Residential' : 'Commercial'}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FORM_FIELDS.map(f => (
              <div key={f.key} className={f.key === 'address' || f.key === 'project_name' ? 'md:col-span-2' : ''}>
                <label className="text-xs text-gray-500 font-semibold tracking-wide mb-1 block">
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </label>
                <input
                  type={f.type || 'text'}
                  value={client[f.key]}
                  onChange={(e) => updateClient(f.key, e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:border-red-500 focus:outline-none"
                  data-testid={`fp-${f.key}`}
                />
              </div>
            ))}
          </div>
          <button type="submit" className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="fp-form-continue">
            Continue to Floor Plan <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    );
  }

  // ---- STEP 2: BUILDER ----
  const allSymbols = Object.entries(SYMBOL_LIBRARY).filter(([k, s]) => s.category === activeCategory);

  return (
    <div className="space-y-4" data-testid="floorplan-builder-step">
      {/* Top toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setStep('form')} className="btn-secondary text-sm flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Edit Client</button>
          <div>
            <h1 className="font-heading text-xl font-bold text-white">{client.project_name}</h1>
            <p className="text-xs text-gray-500">{client.first_name} {client.last_name} • {client.address}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runEstimate} disabled={estimating || pins.length === 0} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 disabled:opacity-40" data-testid="fp-ai-estimate">
            {estimating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}AI Estimate
          </button>
          <button onClick={previewPDF} disabled={previewing || !bgImage} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 disabled:opacity-40" data-testid="fp-preview-pdf">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}Preview PDF
          </button>
          <button onClick={() => exportPDF(false)} disabled={generating || !bgImage} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40" data-testid="fp-download-pdf">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download PDF
          </button>
          <button onClick={() => setEmailModalOpen(true)} disabled={emailing || !bgImage || !client.email} title={!client.email ? 'Client email required' : 'Email PDF to client'} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 disabled:opacity-40" data-testid="fp-email-pdf">
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Email to Client
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* Symbol panel */}
        <div className="col-span-12 lg:col-span-3 space-y-2">
          <div className="card p-3">
            <p className="text-xs font-bold text-gray-400 mb-2 tracking-wide">CATEGORY</p>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(SYMBOL_CATEGORIES).map(([k, c]) => (
                <button key={k} onClick={() => setActiveCategory(k)}
                  className={`p-2 rounded text-[10px] font-medium leading-tight border transition-all ${activeCategory === k ? 'bg-red-600 border-red-500 text-white' : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-700'}`}
                  data-testid={`fp-cat-${k}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div className="card p-2 max-h-[480px] overflow-y-auto">
            <p className="text-xs font-bold text-gray-400 px-1 py-2 tracking-wide">SYMBOLS</p>
            <div className="grid grid-cols-2 gap-1.5">
              {allSymbols.map(([key, s]) => (
                <button key={key} onClick={() => startPlace(key)}
                  className={`p-2 rounded border transition-all flex flex-col items-center gap-1 ${placingSymbol === key ? 'bg-red-600 border-red-500' : 'bg-gray-900 border-gray-800 hover:border-gray-600'}`}
                  data-testid={`fp-sym-${key}`}>
                  <div className="bg-white rounded p-0.5"><SymbolSVG symbolKey={key} size={32} /></div>
                  <span className="text-[9px] text-gray-300 leading-tight text-center">{s.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Canvas area */}
        <div className="col-span-12 lg:col-span-6 space-y-2">
          {/* Tool bar */}
          <div className="card p-2 flex items-center gap-1 flex-wrap">
            {TOOLS.map(t => (
              <button key={t.id} onClick={() => { setActiveTool(t.id); setPlacingSymbol(null); }}
                className={`p-2 rounded flex items-center gap-1 text-xs ${activeTool === t.id ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                title={t.label} data-testid={`fp-tool-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
            <div className="flex-1" />
            {selectedPin && (
              <>
                <button onClick={() => adjustScale(-0.15)} className="p-2 rounded bg-gray-800 hover:bg-gray-700 text-white" data-testid="fp-shrink"><Minus className="w-3.5 h-3.5" /></button>
                <button onClick={() => adjustScale(0.15)} className="p-2 rounded bg-gray-800 hover:bg-gray-700 text-white" data-testid="fp-grow"><Plus className="w-3.5 h-3.5" /></button>
                <button onClick={rotateSelected} className="p-2 rounded bg-gray-800 hover:bg-gray-700 text-white" data-testid="fp-rotate"><RotateCw className="w-3.5 h-3.5" /></button>
              </>
            )}
            {selectedAnnot?.type === 'text' && (
              <button onClick={editTextAnnot} className="p-2 rounded bg-gray-800 hover:bg-gray-700 text-white text-xs" data-testid="fp-edit-text">Edit</button>
            )}
            {selectedId && (
              <button onClick={deleteSelected} className="p-2 rounded bg-red-900 hover:bg-red-800 text-white" data-testid="fp-delete"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
            {(pins.length > 0 || annotations.length > 0) && (
              <button onClick={clearAll} className="p-2 rounded bg-gray-800 hover:bg-gray-700 text-red-400 text-xs" data-testid="fp-clear-all">Clear All</button>
            )}
          </div>

          {/* The plan canvas */}
          <div className="card overflow-hidden bg-white" style={{ minHeight: '500px' }}>
            {!bgImage ? (
              <div className="h-[500px] flex flex-col items-center justify-center text-gray-500 p-6 text-center">
                <Upload className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm mb-4">Upload your floor plan (PNG, JPG or PDF)</p>
                <label className="btn-primary cursor-pointer inline-flex items-center gap-2" data-testid="fp-upload">
                  {uploadingBg ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                  Choose File
                  <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" />
                </label>
              </div>
            ) : (
              <div
                ref={canvasRef}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                className={`relative w-full select-none ${placingSymbol ? 'cursor-crosshair' : activeTool !== 'select' ? 'cursor-crosshair' : 'cursor-default'}`}
                style={{ aspectRatio: '4/3' }}
                data-testid="fp-canvas"
              >
                <img src={bgImage.url} alt="Floor Plan" className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable={false} />
                {/* Annotations */}
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" style={{overflow:'visible'}}>
                  {annotations.map(a => {
                    if (a.type === 'rect') {
                      const x = Math.min(a.x, a.x2), y = Math.min(a.y, a.y2);
                      const w = Math.abs(a.x2 - a.x), h = Math.abs(a.y2 - a.y);
                      return <rect key={a.id} x={x} y={y} width={w} height={h} fill="none" stroke={a.color} strokeWidth="0.3" vectorEffect="non-scaling-stroke" style={{ pointerEvents: 'stroke' }} onMouseDown={(e) => startDragAnnot(e, a)} className={selectedId === a.id ? 'opacity-100' : 'opacity-80'} />;
                    }
                    if (a.type === 'arrow' || a.type === 'measure') {
                      return (
                        <g key={a.id} style={{ pointerEvents: 'stroke' }} onMouseDown={(e) => startDragAnnot(e, a)}>
                          <line x1={a.x} y1={a.y} x2={a.x2} y2={a.y2} stroke={a.color} strokeWidth="0.3" vectorEffect="non-scaling-stroke" markerEnd={a.type === 'arrow' ? 'url(#arrowhead)' : null} />
                          {a.type === 'measure' && <text x={(a.x + a.x2) / 2} y={(a.y + a.y2) / 2 - 1} fill={a.color} fontSize="2" textAnchor="middle">{Math.round(Math.hypot(a.x2 - a.x, a.y2 - a.y) * 10) / 10}u</text>}
                        </g>
                      );
                    }
                    return null;
                  })}
                  {drawing && (() => {
                    if (drawing.type === 'rect') {
                      const x = Math.min(drawing.x, drawing.x2), y = Math.min(drawing.y, drawing.y2);
                      const w = Math.abs(drawing.x2 - drawing.x), h = Math.abs(drawing.y2 - drawing.y);
                      return <rect x={x} y={y} width={w} height={h} fill="none" stroke="#dc2626" strokeWidth="0.3" vectorEffect="non-scaling-stroke" strokeDasharray="1,1" />;
                    }
                    return <line x1={drawing.x} y1={drawing.y} x2={drawing.x2} y2={drawing.y2} stroke="#dc2626" strokeWidth="0.3" vectorEffect="non-scaling-stroke" strokeDasharray="1,1" />;
                  })()}
                  <defs>
                    <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                      <polygon points="0 0, 6 3, 0 6" fill="#dc2626" />
                    </marker>
                  </defs>
                </svg>
                {/* Text annotations */}
                {annotations.filter(a => a.type === 'text').map(a => (
                  <div key={a.id}
                    onMouseDown={(e) => startDragAnnot(e, a)}
                    onDoubleClick={() => { setSelectedId(a.id); editTextAnnot(); }}
                    className={`absolute text-xs font-bold px-1 rounded cursor-move ${selectedId === a.id ? 'bg-yellow-100 ring-2 ring-red-500' : 'bg-white/80'}`}
                    style={{ left: `${a.x}%`, top: `${a.y}%`, transform: 'translate(-50%, -50%)', color: a.color }}
                    data-testid={`fp-text-${a.id}`}
                  >{a.text}</div>
                ))}
                {/* Pins (symbols) */}
                {pins.map(p => {
                  const def = SYMBOL_LIBRARY[p.symbolKey];
                  if (!def) return null;
                  const size = 30 * p.scale;
                  return (
                    <div key={p.id}
                      data-fp-pin="true"
                      onMouseDown={(e) => startDragPin(e, p)}
                      className={`absolute cursor-move bg-white/95 rounded p-0.5 ${selectedId === p.id ? 'ring-2 ring-red-500 shadow-lg' : 'hover:ring-1 hover:ring-red-300'}`}
                      style={{ left: `${p.x}%`, top: `${p.y}%`, transform: `translate(-50%, -50%) rotate(${p.rotation}deg)` }}
                      title={def.name}
                      data-testid={`fp-pin-${p.id}`}
                    >
                      <SymbolSVG symbolKey={p.symbolKey} size={size} />
                    </div>
                  );
                })}
                {placingSymbol && (
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-3 py-1 rounded shadow">Click on the plan to place: {SYMBOL_LIBRARY[placingSymbol].name}</div>
                )}
              </div>
            )}
          </div>

          {bgImage && (
            <button onClick={() => { if(window.confirm('Replace floor plan? Symbols will be kept.')) { setBgImage(null); } }} className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-1" data-testid="fp-change-bg">
              <X className="w-3 h-3" /> Replace floor plan
            </button>
          )}
        </div>

        {/* Right: Legend + AI */}
        <div className="col-span-12 lg:col-span-3 space-y-2">
          <div className="card p-3">
            <p className="text-xs font-bold text-gray-400 mb-2 tracking-wide">LEGEND ({Object.keys(symbolCounts).length})</p>
            {Object.keys(symbolCounts).length === 0 ? (
              <p className="text-xs text-gray-600 italic">Place symbols to populate the legend.</p>
            ) : (
              <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
                {Object.entries(symbolCounts).map(([key, qty]) => {
                  const s = SYMBOL_LIBRARY[key];
                  return (
                    <div key={key} className="flex items-center gap-2 bg-gray-900/50 p-1.5 rounded">
                      <div className="bg-white rounded p-0.5 shrink-0"><SymbolSVG symbolKey={key} size={24} /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-white truncate font-medium">{s.name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] text-gray-500">$</span>
                          <input type="number" step="0.01" value={priceFor(key)}
                            onChange={(e) => setPriceOverrides(p => ({ ...p, [key]: e.target.value }))}
                            className="w-16 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-[10px] text-white"
                            data-testid={`fp-price-${key}`} />
                          <span className="text-[9px] text-gray-500">/unit</span>
                        </div>
                      </div>
                      <span className="text-xs bg-red-900/40 text-red-300 px-1.5 py-0.5 rounded">×{qty}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {Object.keys(symbolCounts).length > 0 && (() => {
              const { sub, tx, co, total } = calcTotals();
              return (
                <div className="mt-3 pt-3 border-t border-gray-800 text-xs space-y-1">
                  <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>${sub.toFixed(2)}</span></div>
                  <div className="flex items-center justify-between text-gray-500">
                    <span>Tax Rate</span>
                    <div className="flex items-center gap-1">
                      <input type="number" step="0.001" value={taxRate} onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                        className="w-14 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-[10px] text-white text-right" data-testid="fp-tax-rate" />
                      <span>(${tx.toFixed(2)})</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-gray-500">
                    <span>Contingency</span>
                    <div className="flex items-center gap-1">
                      <input type="number" step="0.01" value={contingencyRate} onChange={(e) => setContingencyRate(parseFloat(e.target.value) || 0)}
                        className="w-14 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-[10px] text-white text-right" data-testid="fp-contingency-rate" />
                      <span>(${co.toFixed(2)})</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-red-400 font-bold pt-1 border-t border-gray-800"><span>TOTAL</span><span>${total.toFixed(2)}</span></div>
                  <button onClick={saveAsRequest} disabled={savingRequest} className="w-full mt-2 bg-green-700 hover:bg-green-600 text-white text-xs py-2 rounded flex items-center justify-center gap-1 disabled:opacity-40" data-testid="fp-save-request">
                    {savingRequest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}Save to All Requests
                  </button>
                </div>
              );
            })()}
          </div>

          {estimate && (
            <div className="card p-3 border-purple-700/40" data-testid="fp-ai-result">
              <p className="text-xs font-bold text-purple-400 mb-2 tracking-wide flex items-center gap-1"><Sparkles className="w-3 h-3" />AI ANALYSIS</p>
              <p className="text-[11px] text-gray-300 mb-2">{estimate.summary}</p>
              {estimate.estimated_duration && <p className="text-[11px] text-gray-500">Duration: <span className="text-white">{estimate.estimated_duration}</span></p>}
              {estimate.grand_total && <p className="text-sm text-purple-300 font-bold mt-1">AI Total: ${estimate.grand_total.toFixed?.(2) || estimate.grand_total}</p>}
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closePreview}>
          <div onClick={(e) => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl h-[90vh] flex flex-col" data-testid="fp-preview-modal">
            <div className="px-6 py-3 border-b border-gray-800 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-red-400 font-bold tracking-widest">PARAPAMJE PDF</p>
                <h3 className="text-base font-bold text-white">Verifiko pozicionet e simboleve para shkarkimit</h3>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => exportPDF(false)} className="btn-primary text-sm flex items-center gap-2" data-testid="fp-preview-download">
                  <Download className="w-4 h-4" />Shkarko
                </button>
                <button onClick={closePreview} className="p-2 hover:bg-gray-800 rounded" data-testid="fp-preview-close"><X className="w-4 h-4 text-gray-400" /></button>
              </div>
            </div>
            <div className="flex-1 bg-gray-950 overflow-hidden">
              <iframe src={previewUrl} title="Floor Plan PDF Preview" className="w-full h-full border-0" data-testid="fp-preview-iframe" />
            </div>
          </div>
        </div>
      )}

      {/* Email PDF Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEmailModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full" data-testid="fp-email-modal">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-blue-400 font-bold tracking-widest">EMAIL FLOOR PLAN</p>
                <h3 className="text-base font-bold text-white">Send to client</h3>
              </div>
              <button onClick={() => setEmailModalOpen(false)} className="p-1 hover:bg-gray-800 rounded"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="text-[10px] uppercase tracking-wider font-bold w-12">From</span>
                  <span className="text-white">info@bellajaelectric.com</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="text-[10px] uppercase tracking-wider font-bold w-12">To</span>
                  <span className="text-white">{client.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="text-[10px] uppercase tracking-wider font-bold w-12">CC</span>
                  <span className="text-white">info@bellajaelectric.com (you, as a copy)</span>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">PERSONAL MESSAGE (optional)</label>
                <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} rows={4}
                  placeholder="Hi, here is your floor plan estimate. Let me know if you have any questions."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:border-red-500 focus:outline-none"
                  data-testid="fp-email-message" />
              </div>
              <div className="bg-gray-800/50 border border-gray-800 rounded p-2 text-xs text-gray-400">
                <strong className="text-gray-300">Attachment:</strong> BELLAJA-FloorPlan-{client.project_name.replace(/\s+/g, '_')}.pdf (3 pages)
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-800 flex gap-2 justify-end">
              <button onClick={() => setEmailModalOpen(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={emailPDFToClient} disabled={emailing} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 disabled:opacity-40" data-testid="fp-confirm-send-email">
                {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFloorPlanBuilder;
